import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPaperclip, FaTimes } from 'react-icons/fa';

/**
 * Choix des pièces jointes d'une demande (Yros/Kavero#431).
 *
 * Facultatif : aucune pièce n'est jamais exigée ici — c'est ce qui le
 * distingue du justificatif de la note de frais. Un seul composant pour le
 * congé, l'avance et le prêt, comme le socle d'Odoo n'a qu'un mécanisme.
 *
 * Chaque choix de fichiers s'ajoute à la liste au lieu de la remplacer, et
 * chaque pièce se retire avant l'envoi. L'écran ne juge que ce qu'il voit :
 * `verifierPieces` écarte le trop-plein, le format apparent et le poids ; le
 * type réel du fichier, lu dans son contenu, est jugé par Odoo, dont le refus
 * s'affiche mot pour mot.
 */

const TYPES_ACCEPTES = ['application/pdf', 'image/jpeg', 'image/png'];
const EXTENSIONS_ACCEPTEES = /\.(pdf|jpe?g|png)$/i;
const TAILLE_MAX = 5 * 1024 * 1024;
const NOMBRE_MAX = 5;

/** Une pièce se reconnaît à son type, ou à défaut à son extension : certains
 * navigateurs ne renseignent pas le type d'une photo prise au téléphone. */
function estAcceptee(fichier) {
  return TYPES_ACCEPTES.includes(fichier.type)
    || (!fichier.type && EXTENSIONS_ACCEPTEES.test(fichier.name));
}

/** Le message du premier défaut visible, ou null si les pièces peuvent partir. */
export function verifierPieces(fichiers, t) {
  if (fichiers.length > NOMBRE_MAX) return t('piecesJointes.tropDeFichiers');
  const refuse = fichiers.find((fichier) => !estAcceptee(fichier));
  if (refuse) return t('piecesJointes.formatInvalide', { nom: refuse.name });
  const lourd = fichiers.find((fichier) => fichier.size > TAILLE_MAX);
  if (lourd) return t('piecesJointes.tropLourd', { nom: lourd.name });
  return null;
}

/** « 850 ko », « 1,2 Mo » : la taille d'un fichier dans la langue de l'écran. */
function formaterTaille(octets, locale) {
  const [valeur, unite] = octets < 1024 * 1024
    ? [Math.max(1, Math.round(octets / 1024)), 'kilobyte']
    : [octets / (1024 * 1024), 'megabyte'];
  return new Intl.NumberFormat(locale, {
    style: 'unit', unit: unite, unitDisplay: 'short', maximumFractionDigits: 1,
  }).format(valeur);
}

function memeFichier(a, b) {
  return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
}

function SelecteurPieces({ fichiers, onChange, idPrefix }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';
  const champ = useRef(null);

  const ajouter = (event) => {
    const choisis = Array.from(event.target.files || []);
    onChange([
      ...fichiers,
      ...choisis.filter((fichier) => !fichiers.some((deja) => memeFichier(deja, fichier))),
    ]);
    // Vidé après chaque choix : une pièce retirée doit pouvoir être rechoisie.
    event.target.value = '';
  };

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium" htmlFor={`${idPrefix}-justificatif`}>
        {t('piecesJointes.titre')}
      </label>
      <input
        ref={champ}
        id={`${idPrefix}-justificatif`}
        type="file"
        multiple
        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
        data-testid={`${idPrefix}-justificatif`}
        onChange={ajouter}
        className="block w-full text-sm text-slate-700 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-4 file:py-2 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200"
      />
      <p className="text-xs text-slate-500">{t('piecesJointes.aide')}</p>
      {fichiers.length > 0 && (
        <ul data-testid={`${idPrefix}-pieces`} className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {fichiers.map((fichier, index) => (
            <li key={`${fichier.name}-${fichier.size}-${fichier.lastModified}`} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span className="flex min-w-0 items-center gap-2">
                <FaPaperclip className="flex-shrink-0 text-slate-400" aria-hidden="true" />
                <span className="break-all text-slate-700">{fichier.name}</span>
                <span className="flex-shrink-0 text-xs text-slate-500 tabular-nums">{formaterTaille(fichier.size, locale)}</span>
              </span>
              <button
                type="button"
                data-testid={`${idPrefix}-retirer-${index}`}
                onClick={() => onChange(fichiers.filter((_, position) => position !== index))}
                aria-label={t('piecesJointes.retirer', { nom: fichier.name })}
                title={t('piecesJointes.retirer', { nom: fichier.name })}
                className="flex-shrink-0 rounded p-2 text-slate-500 hover:bg-slate-100 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-[--color-primary]"
              >
                <FaTimes aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default SelecteurPieces;
